import { Link, useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import ManagerLayout from "../../layouts/ManagerLayout";
import { createProjectDiscipline } from "../../api/projectService";

function CreateDiscipline() {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    disciplineId: "",
    estimatedHours: "",
    startDate: "",
    description: ""
  });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.disciplineId.trim()) {
      alert("Discipline ID is required");
      return;
    }
    setLoading(true);
    try {
      await createProjectDiscipline(projectId, {
        discipline_id: formData.disciplineId.trim().toUpperCase(),
        estimated_hours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : 0.0,
      });
      alert("Discipline created successfully!");
      navigate(`/manager/discipline-details/${projectId}`);
    } catch (err) {
      alert(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        "Failed to create discipline."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ManagerLayout>
      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1400px] mx-auto">

          {/* HEADER */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto mb-6 sm:mb-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-2xl sm:text-3xl">🎯</span>
                </div>
                <div>
                  <h1 className="text-xl sm:text-3xl font-bold text-white">Create New Discipline</h1>
                  <p className="text-white/60 text-xs sm:text-sm">
                    Add a new discipline to Project {projectId}
                  </p>
                </div>
              </div>

              {/* Bulk Upload */}
              <Link to={`/manager/projects/${projectId}/disciplines/bulk-create`} className="self-start sm:self-auto">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl border-2 border-cyan-400/60 bg-cyan-500/15 text-cyan-200 font-semibold text-xs sm:text-sm backdrop-blur-sm hover:bg-cyan-500/25 hover:border-cyan-300 transition-all shadow-lg whitespace-nowrap"
                >
                  <span className="text-sm sm:text-base">📊</span>
                  Bulk Upload
                </motion.button>
              </Link>
            </div>
          </motion.div>

          {/* FORM CARD */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-8 max-w-3xl mx-auto"
          >
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              <div>
                <label className="block text-white font-semibold mb-2 text-sm">
                  Discipline ID
                </label>
                <input
                  type="text"
                  name="disciplineId"
                  required
                  value={formData.disciplineId}
                  onChange={handleChange}
                  placeholder="Enter unique discipline identifier"
                  className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm sm:text-base placeholder-white/40 focus:outline-none focus:border-cyan-400 transition"
                />
              </div>

              <div>
                <label className="block text-white font-semibold mb-2 text-sm">
                  Estimated Hours
                </label>
                <input
                  type="number"
                  name="estimatedHours"
                  value={formData.estimatedHours}
                  onChange={handleChange}
                  placeholder="Enter estimated hours"
                  min="0"
                  className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm sm:text-base placeholder-white/40 focus:outline-none focus:border-cyan-400 transition"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold shadow-lg disabled:opacity-60 hover:opacity-90 transition text-sm sm:text-base"
              >
                {loading ? "Creating..." : "Create Discipline"}
              </button>
            </form>
          </motion.div>

          {/* BACK */}
          <div className="max-w-3xl mx-auto mt-4 flex justify-start">
            <Link
              to={`/manager/discipline-details/${projectId}`}
              className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl border-2 border-white/40 text-white hover:bg-white/10 text-sm transition"
            >
              ← Back to Projects
            </Link>
          </div>

        </div>
      </div>
    </ManagerLayout>
  );
}

export default CreateDiscipline;
